import { NavigateDown, NavigateUp } from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { Button } from '../Button/Button';
import { Checkbox } from '../Forms';
import { InnerRadioButton } from '../Forms/RadioGroup/InnerRadioButton';
import { StyledTable, TableBody, TableHead, TableHeader, TableRow } from '../StyledTable';

import { ColumnFilterPopup } from './ColumnFilterPopup';
import { isFilterHandled } from './ColumnFilterPopup/ColumnFilterPopup.types';
import styles from './DataTable.module.scss';
import { DataTableProps, getColumnCanGlobalFilter, globalFilterFn } from './DataTable.types';
import { DataTableRow } from './DataTableRow';

const DataTableEmptyRow = ({ children }: PropsWithChildren) => {
  return (
    <div className={styles.emptyRowContainer}>
      <div className={styles.emptyRow}>{children}</div>
    </div>
  );
};

export const DataTableInner = <TData, TValue extends { name: 'name' }>(
  {
    color,
    headerColor,
    size,
    stickyHeader,
    rowStyle = 'classic',
    className,
    containerClassName,
    columns,
    data,
    onRowClick,
    navigateTo,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    onPaginationChange,
    empty,
    hideHeaders,
    hideHeadersIfEmpty,
    enablePagination = false,
    enableRowSelection = false,
    enableExpanding = false,
    hideDefaultExpandColumn,
    hideDefaultSelectionColumn,
    disableExpandOnClick,
    disableSelectOnClick,
    disableMultiExpansion,
    disableRowAnimation,
    getSubRows,
    defaultColumn,
    state,
    ...options
  }: DataTableProps<TData>,
  ref: ForwardedRef<Table<TData>>
) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: options.initialState?.pagination?.pageIndex ?? 0,
    pageSize: options.initialState?.pagination?.pageSize ?? 20,
  });

  if (rowStyle === 'cards') {
    disableRowAnimation = true;
  }

  const onExpandedChange = useCallback(
    (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
      const oldValue = expanded;
      const newValue = typeof updater === 'function' ? updater(oldValue) : updater;
      if (disableMultiExpansion && Object.keys(newValue).length > 1) {
        for (const key of Object.keys(oldValue)) {
          if (Object.keys(newValue).includes(key)) delete (newValue as Record<string, true>)[key];
        }
      }
      setExpanded(newValue);
    },
    [setExpanded, expanded, disableMultiExpansion]
  );

  const allColumns = useMemo(() => {
    const expandColumn: ColumnDef<TData, TValue> = {
      id: 'expand',
      size: 52,
      minSize: 52,
      maxSize: 52,
      cell: ({ row }) =>
        row.getCanExpand() && (
          <Button
            variant="tertiary"
            size="medium"
            data-full-height
            data-no-padding-right
            onClick={() => row.toggleExpanded()}
          >
            {row.getIsExpanded() ? <NavigateUp /> : <NavigateDown />}
          </Button>
        ),
    };

    const selectColumn: ColumnDef<TData, TValue> = {
      id: 'select',
      header: ({ table }) => {
        if (table.options.enableMultiRowSelection === false) return null;
        return (
          <div
            onClick={() =>
              table.toggleAllPageRowsSelected(
                !table.getIsAllPageRowsSelected() && !table.getIsSomePageRowsSelected()
              )
            }
          >
            <Checkbox
              data-full-height
              data-no-padding-right
              checked={table.getIsAllPageRowsSelected()}
              halfChecked={table.getIsSomePageRowsSelected()}
              onChange={() => {}}
              aria-label="Select all"
            />
          </div>
        );
      },
      cell: ({ row, table }) => {
        if (!row.getCanSelect()) return null;
        if (table.options.enableMultiRowSelection === false)
          return (
            <div data-full-height data-no-padding-right>
              <InnerRadioButton
                checked={row.getIsSelected()}
                onChange={checked => row.toggleSelected(checked)}
              />
            </div>
          );
        return (
          <div onClick={event => event.stopPropagation()} style={{ display: 'contents' }}>
            <Checkbox
              data-full-height
              data-no-padding-right
              checked={row.getIsSelected()}
              onClick={event => event.stopPropagation()}
              onChange={() => row.toggleSelected()}
              aria-label="Select row"
            />
          </div>
        );
      },
      size: 44,
      minSize: 44,
      maxSize: 44,
      enableSorting: false,
    };
    return [
      enableExpanding && !hideDefaultExpandColumn && expandColumn,
      enableRowSelection && !hideDefaultSelectionColumn && selectColumn,
      ...columns,
    ].filter(Boolean);
  }, [
    columns,
    enableRowSelection,
    enableExpanding,
    hideDefaultExpandColumn,
    hideDefaultSelectionColumn,
  ]);

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: sorting => {
      setSorting(sorting);
      onSortingChange?.(sorting);
    },
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: columnFilters => {
      setColumnFilters(columnFilters);
      onColumnFiltersChange?.(columnFilters);
    },
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getSubRows: getSubRows,
    onExpandedChange,
    getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
    onColumnVisibilityChange: columnVisibility => {
      setColumnVisibility(columnVisibility);
      onColumnVisibilityChange?.(columnVisibility);
    },
    enableRowSelection,
    onRowSelectionChange: rowSelection => {
      setRowSelection(rowSelection);
      onRowSelectionChange?.(rowSelection);
    },
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onPaginationChange: pagination => {
      if (!enablePagination) return;
      setPagination(pagination);
      onPaginationChange?.(pagination);
    },
    filterFns: {
      arrIncludes: (row, columnId, filterValue?: (string | number)[]) =>
        filterValue?.includes(row.getValue(columnId)) ?? true,
    },
    globalFilterFn,
    getColumnCanGlobalFilter,
    defaultColumn: {
      size: undefined,
      minSize: undefined,
      maxSize: undefined,
      ...defaultColumn,
    },
    state: {
      expanded,
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(enablePagination ? { pagination } : undefined),
      ...state,
    },
    ...options,
  });

  useImperativeHandle(ref, () => table, [table]);

  const rowModel = table.getRowModel();

  return hideHeadersIfEmpty && !rowModel.rows.length ? (
    <>{empty}</>
  ) : (
    <StyledTable
      color={color}
      size={size}
      stickyHeader={stickyHeader}
      className={className}
      containerClassName={containerClassName}
    >
      {!hideHeaders && (
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow color={headerColor} key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const size = header.column.columnDef.size;
                const minSize = header.column.columnDef.minSize;
                const maxSize = header.column.columnDef.maxSize;

                return (
                  <TableHead
                    key={header.id}
                    style={{
                      width: size && `${size}px`,
                      minWidth: minSize && `${minSize}px`,
                      maxWidth: maxSize && `${maxSize}px`,
                    }}
                  >
                    <div className={styles.filterHeader} data-no-padding-right>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {isFilterHandled(header.column) || header.column.getCanSort() ? (
                        <>
                          <div className={styles.spacer} />
                          <ColumnFilterPopup
                            column={header.column}
                            className={styles.filterButton}
                          />
                        </>
                      ) : null}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
      )}
      <TableBody>
        {rowModel.rows.length
          ? rowModel.rows
              .filter(row => {
                if (!disableRowAnimation && rowModel.flatRows.length < 200) {
                  return row.depth === 0;
                }
                return true;
              })
              .map(row => (
                <DataTableRow
                  key={row.id}
                  rowStyle={rowStyle}
                  table={table}
                  row={row}
                  alternateBackgrounds={color === 'mixed'}
                  disableExpandOnClick={disableExpandOnClick}
                  enableExpanding={enableExpanding}
                  enableRowSelection={enableRowSelection}
                  disableSelectOnClick={disableSelectOnClick}
                  disableRowAnimation={disableRowAnimation}
                  onRowClick={onRowClick}
                  navigateTo={navigateTo}
                />
              ))
          : null}
      </TableBody>
      {rowModel.rows.length ? null : <DataTableEmptyRow>{empty}</DataTableEmptyRow>}
    </StyledTable>
  );
};

export const DataTable = forwardRef(DataTableInner) as <TData>(
  props: DataTableProps<TData> & { ref?: Ref<Table<TData>> }
) => ReturnType<typeof DataTableInner>;
